import { Button, MessageModal, ModalProps } from "@blacknut/react-sdk";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AppLayout,
  NotificationMessage,
  NotificationMessageKind,
  State,
  UserAction,
  getSubscription,
  notificationService,
  useLayout,
  useThunkDispatch,
} from "core";
import { useSelector } from "react-redux";
import { useWarioURL } from "src/utils/useWarioURL";
import {
  GamePass,
  RemoteNotificationTrigger,
  Status,
} from "@blacknut/javascript-sdk/dist";
import { useTheme } from "src/theme/ThemeProvider";
import { logD, logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import { useSignupURL } from "src/utils/useSignupURL";
import { QRCodeModal } from "../QRCodeModal";
import { PassModal } from "src/utils/useGamePassUnlock/useGamePassUnlock";
import { isTryBuy } from "src/utils/subscription";

export interface NotSubscribedProps extends ModalProps {
  isProfileMaster: boolean;
  subscribeURL?: string;
  onClose: () => void;
  onSubscribe: () => void;
  onPlanSubscribed: () => void;
  gamePass?: GamePass;
}

const TVModal = (props: NotSubscribedProps) => {
  const { t } = useTranslation();
  const { isProfileMaster, subscribeURL, gamePass } = props;
  const { accountUrl, organization, billingInfo } = useSelector(
    (state: State) => state.globalState,
  );
  const subStatus = billingInfo?.subscription?.status;
  const { uri: subURL } = useSignupURL();
  const { create } = useWarioURL();
  const { theme } = useTheme();
  const [URL, setURL] = useState("");
  const layout = useLayout();
  const _isTryBuy = isTryBuy(billingInfo.subscription);

  useEffect(() => {
    const isRedirectToWario =
      subStatus === Status.PAST_DUE ||
      subStatus === Status.SUSPENDED ||
      subStatus === Status.INCOMPLETE;

    const getWarioURL = async () => {
      const warioUrl = await create(organization?.subscriptionUrl, undefined, {
        tokens: isProfileMaster,
      });
      setURL(warioUrl.toString());
    };
    if (subscribeURL) {
      setURL(subscribeURL);
    } else if (organization?.subscriptionUrl || isRedirectToWario) {
      getWarioURL();
    } else {
      setURL(subURL.toString());
    }
  }, [
    accountUrl,
    subURL,
    create,
    isProfileMaster,
    organization?.subscriptionUrl,
    subscribeURL,
    subStatus,
  ]);

  const getDescription = useCallback(() => {
    if (subStatus === Status.PAST_DUE) return t("account.subscriptionStatus.past_dueTV");
    if (subStatus === Status.SUSPENDED && organization?.name)
      return t("account.subscriptionStatus.suspendedTV", {
        organizationName: organization.name,
      });
    if (subStatus === Status.INCOMPLETE)
      return t("account.subscriptionStatus.incompleteTV");

    return t("account.subscriptionStatusDescTV");
  }, [organization?.name, subStatus, t]);

  if (gamePass && (_isTryBuy || layout === AppLayout.TV)) {
    logD(LOGGING_TAG, "Using url: %o", URL);
    return (
      <PassModal
        {...props}
        gamePass={gamePass}
        url={URL}
        title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
        description={
          layout === AppLayout.TV
            ? t("dialogs.pass.tvSubscribe.message")
            : t("dialogs.pass.subscribe.message")
        }
      />
    );
  }

  return (
    <QRCodeModal
      {...props}
      title={t("account.subscriptionStatusTitleTV")}
      description={getDescription()}
      btnText={t("dialogs.error.notSubscribed.no")}
      URL={URL}
      theme={theme}
    />
  );
};

const NotSubscribedModal = (props: NotSubscribedProps) => {
  const { t } = useTranslation();
  const {
    isProfileMaster,
    onSubscribe,
    onClose,
    onPlanSubscribed,
    gamePass,
    subscribeURL,
  } = props;
  const isTV = useLayout() === AppLayout.TV;
  const dispatch = useThunkDispatch<UserAction>();
  const { billingInfo, organization } = useSelector((state: State) => state.globalState);
  const subStatus = billingInfo?.subscription?.status;

  const _onPlanSubscribed = useCallback(async () => {
    try {
      //Refresh sub
      await dispatch(getSubscription());
      onPlanSubscribed();
    } catch (e) {
      logE(LOGGING_TAG, "Caught error getting subscription");
    }
  }, [dispatch, onPlanSubscribed]);

  useEffect(() => {
    const sub = notificationService.onMessage().subscribe((n: NotificationMessage) => {
      if (n.kind !== NotificationMessageKind.NOTIFICATION_RESP) return;
      const { notification } = n.payload;

      if (notification.trigger === RemoteNotificationTrigger.PLAN_SUBSCRIBED) {
        _onPlanSubscribed();
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [_onPlanSubscribed]);

  const getMessage = useCallback(() => {
    if (subStatus === Status.PAST_DUE) return t("account.subscriptionStatus.past_due");
    if (subStatus === Status.SUSPENDED && organization?.name)
      return t("account.subscriptionStatus.suspended", {
        organizationName: organization.name,
      });
    if (subStatus === Status.INCOMPLETE)
      return t("account.subscriptionStatus.incomplete");

    return t("account.subscriptionStatusDesc");
  }, [organization?.name, subStatus, t]);

  if (isTV) {
    return <TVModal {...props} />;
  }

  if (gamePass) {
    logD(LOGGING_TAG, "Using subscribeUrl: %o", subscribeURL);
    return (
      <PassModal
        {...props}
        gamePass={gamePass}
        url={subscribeURL || ""}
        title={t("dialogs.pass.subscribe.title", { passName: gamePass.name })}
        description={t("dialogs.pass.subscribe.message")}
      />
    );
  }
  return (
    <MessageModal
      {...props}
      title={t("account.subscriptionStatusTitle")}
      message={getMessage()}
      onClose={onClose}
      buttons={
        isProfileMaster && [
          <Button variant="secondary" key="cancel" onClick={onClose}>
            {t("dialogs.error.notSubscribed.no")}
          </Button>,
          <Button variant="primary" key="subscribe" onClick={onSubscribe}>
            {t("buttons.subscribe")}
          </Button>,
        ]
      }
    />
  );
};

export default NotSubscribedModal;
